import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { interval, Subscription } from 'rxjs';
import { MERK_DOMAIN } from '../../_helpers/Constants';
import { AuthInfo } from '../../_models/AuthInfo';
import { LoginDTO } from '../../_models/Login';
import { AuthService } from '../../_services/auth.service';
import { MerckAuthService } from '../../_services/merck-auth.service';
import { MicrosoftAuthService } from '../../_services/microsoft-auth.service';
import { SessionGuardService } from '../../_services/session-guard.service';
import { TokenService } from '../../_services/token.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LoginComponent {
  loginForm: FormGroup;
  isError: boolean = false;
  isMicrosoftLoginInProgress: boolean = false;
  isMerckLoginInProgress: boolean = false;
  isCheckingEmail: boolean = false;
  loginStep: 'email' | 'password' | 'microsoft' | 'merck' = 'email';
  userEmail: string = '';

  model: LoginDTO = {
    email: '',
    password: '',
  };

  isPasswordVisible: boolean = false;
  isTwoFactorLogin: boolean = false;
  private totalTime: number = 90;
  public minutes: number = 1;
  public seconds: number = 30;
  private timerSubscription: Subscription | undefined;
  public isRunning: boolean = false;
  isWrongCode = false;
  isEmailPending = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private tokenStorage: TokenService,
    private toastr: ToastrService,
    private microsoftAuthService: MicrosoftAuthService,
    private merckAuthService: MerckAuthService,
    private sessionGuard: SessionGuardService
  ) {
    this.loginForm = this.fb.group({
      email: [this.model.email, [Validators.required, Validators.email]],
      password: [this.model.password], // Not required initially
    });
  }

  /**
   * Step 1: Check email and determine authentication method
   * Priority: Merck SSO -> Microsoft SSO -> Password
   */
  checkEmailAndProceed() {
    const emailControl = this.loginForm.get('email');

    if (emailControl?.invalid) {
      emailControl.markAsTouched();
      return;
    }

    this.isCheckingEmail = true;
    this.isError = false;
    this.userEmail = emailControl?.value;

    // Step 1: Check if user is from Merck domain (including subdomains like @one.merckgroup.com)
    const emailLower = this.userEmail.toLowerCase();
    if (
      emailLower.endsWith('@' + MERK_DOMAIN) ||
      (emailLower.includes('@') && emailLower.split('@')[1].endsWith('.' + MERK_DOMAIN))
    ) {
      this.isCheckingEmail = false;
      this.loginStep = 'merck';
      setTimeout(() => {
        this.loginWithMerck();
      }, 500);
      return;
    }

    // Step 2: Check if user exists in Microsoft Azure AD
    this.microsoftAuthService.checkEmailInAzureAd(this.userEmail).subscribe({
      next: (response) => {
        this.isCheckingEmail = false;

        if (response.existsInAzureAd) {
          // User exists in Azure AD - trigger Microsoft login
          this.loginStep = 'microsoft';
          setTimeout(() => {
            this.loginWithMicrosoft();
          }, 500);
        } else {
          // Step 3: Show password field for local login
          this.loginStep = 'password';
          this.loginForm.get('password')?.setValidators([Validators.required]);
          this.loginForm.get('password')?.updateValueAndValidity();
        }
      },
      error: (error) => {
        this.isCheckingEmail = false;
        console.error('Error checking email:', error);
        // If check fails, default to password login
        this.loginStep = 'password';
        this.loginForm.get('password')?.setValidators([Validators.required]);
        this.loginForm.get('password')?.updateValueAndValidity();
      },
    });
  }

  /**
   * Go back to email step
   */
  goBackToEmail() {
    this.loginStep = 'email';
    this.isError = false;
    this.loginForm.get('password')?.clearValidators();
    this.loginForm.get('password')?.updateValueAndValidity();
    this.loginForm.get('password')?.setValue('');
  }

  /**
   * Step 2: Submit with password (traditional login)
   */
  async onSubmit(): Promise<void> {
    // If on email step, check email first
    if (this.loginStep === 'email') {
      this.checkEmailAndProceed();
      return;
    }

    // If on password step, proceed with login
    if (!this.loginForm.valid || !(await this.sessionGuard.confirmSessionChange())) return;
    this.model.email = this.loginForm.controls['email'].value;
    this.model.password = this.loginForm.controls['password'].value;
    this.authService.Login(this.model).subscribe((data) => {
      if (data.success) {
        this.completeLogin(data);
      } else if (data.twoFactorResult) {
        this.isTwoFactorLogin = true;
        this.startTimer();
        this.toastr.success('Ein einmaliges Passwort wird an Ihre E-Mail gesendet.');
      } else if (data.isNotAllowed) {
        this.isEmailPending = true;
      } else {
        this.isError = true;
      }
    });
  }

  GoToRegister() {
    this.router.navigateByUrl('/signup');
  }

  GoToForgotPassword() {
    this.router.navigateByUrl('/recovery-password');
  }

  onOtpChange($event: string) {
    this.isWrongCode = false;
    this.model.password = $event;
  }

  // The user already confirmed the session change at the password step.
  loginTwoFactor() {
    this.authService
      .LoginTwoFactor({
        twoFactorCode: this.model.password,
        email: this.model.email,
      })
      .subscribe((data) => {
        if (data.success) {
          this.completeLogin(data);
        } else {
          this.isWrongCode = true;
        }
      });
  }

  startTimer() {
    if (!this.isRunning) {
      this.isRunning = true;
      this.timerSubscription = interval(1000).subscribe(() => {
        if (this.totalTime > 0) {
          this.totalTime--;
          this.minutes = Math.floor(this.totalTime / 60);
          this.seconds = this.totalTime % 60;
        } else {
          this.toastr.info('Das Passwort ist abgelaufen, bitte fordern Sie einen neuen Code an.');
          this.stopTimer();
        }
      });
    }
  }

  stopTimer() {
    this.isRunning = false;
    this.timerSubscription?.unsubscribe();
  }

  resetTimer() {
    this.stopTimer();
    this.totalTime = 90;
    this.minutes = 1;
    this.seconds = 30;
  }

  resendEmail(): void {
    this.authService.reSendEmailConfirmation(this.loginForm.get('email')!.value).subscribe(() => {
      this.toastr.info('Bestätigung wurde erfolgreich an Ihre E-Mail gesendet');
    });
  }

  /**
   * Login with Microsoft account
   */
  async loginWithMicrosoft(): Promise<void> {
    if (!(await this.sessionGuard.confirmSessionChange())) {
      this.goBackToEmail();
      return;
    }
    this.isMicrosoftLoginInProgress = true;
    this.isError = false;

    this.microsoftAuthService.loginWithMicrosoft(this.userEmail).subscribe({
      next: (data) => {
        if (data.success) {
          this.completeLogin(data, 'Erfolgreich mit Microsoft angemeldet');
        } else {
          this.isError = true;
          this.toastr.error('Microsoft-Anmeldung fehlgeschlagen');
        }
        this.isMicrosoftLoginInProgress = false;
      },
      error: (error) => {
        console.error('Microsoft login error:', error);
        this.isError = true;
        this.isMicrosoftLoginInProgress = false;
        this.toastr.error('Microsoft-Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.');
      },
    });
  }

  /**
   * Login with Merck SSO
   */
  async loginWithMerck(): Promise<void> {
    if (!(await this.sessionGuard.confirmSessionChange())) {
      this.goBackToEmail();
      return;
    }
    this.isMerckLoginInProgress = true;
    this.isError = false;
    this.loginStep = 'merck';

    this.merckAuthService.loginWithMerck(this.userEmail).subscribe(
      (data: AuthInfo) => {
        if (data.success) {
          this.completeLogin(data, 'Erfolgreich mit Merck SSO angemeldet');
        } else {
          this.isError = true;
          this.toastr.error('Merck SSO-Anmeldung fehlgeschlagen');
          this.goBackToEmail();
        }
        this.isMerckLoginInProgress = false;
      },
      (error: unknown) => {
        console.error('Merck login error:', error);
        this.isError = true;
        this.isMerckLoginInProgress = false;
        if (!(error instanceof Error) || error.message !== 'Login cancelled') {
          this.toastr.error('Merck SSO-Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.');
        }
        this.goBackToEmail();
      }
    );
  }

  private completeLogin(data: AuthInfo, successMessage?: string): void {
    const { user } = data;
    const displayName = user.firstName ? `${user.firstName} ${user.lastName ?? ''}`.trim() : user.email;
    this.tokenStorage.startSession(data.accessToken, data.refreshToken, user.id, displayName, user.email);

    const redirectUrl = localStorage.getItem('redirectUrl');
    localStorage.removeItem('redirectUrl');
    this.router.navigateByUrl(redirectUrl && redirectUrl !== '/login' ? redirectUrl : '/home');
    if (successMessage) {
      this.toastr.success(successMessage);
    }
  }
}
